import { gql, TypedDocumentNode } from "@apollo/client";

import {
  CasePropertiesQueryQuery,
  CasePropertiesQueryQueryVariables,
} from "src/graphql/anti-fraud/__generated__/graphql";
import { elementTypeFragment } from "src/graphql/anti-fraud/fragments/ElementTypeFragment";
import { propertyFragment } from "src/graphql/anti-fraud/fragments/PropertyFragment";
import { useFetchQuery } from "src/graphql/utils";
import { mapToPropertyCode } from "src/mappers/property.mappers";
import { mapDataTypeToAppDataType } from "src/mappers/typedValue.mappers";
import { ClientSchemaProperty } from "src/types/clientSchema";
import { isEmpty } from "src/utils/arrays.utils.ts";
export const casePropertiesQuery = gql /* GraphQL */ `
  query CasePropertiesQuery {
    schema {
      caseTypes {
        name
        code
        properties {
          ...PropertyFragment
        }
        elements {
          name
          code
          elementType {
            ...ElementTypeFragment
          }
        }
      }
    }
  }
  ${propertyFragment}
  ${elementTypeFragment}
` as TypedDocumentNode<CasePropertiesQueryQuery, CasePropertiesQueryQueryVariables>;

export const useGetCasePropertiesQuery = ({
  onError,
  caseTypeCode,
}: {
  onError: (error: Error) => void;
  caseTypeCode?: string;
}) => {
  return useFetchQuery(casePropertiesQuery, {
    toData: (data) => [
      ...(data?.schema?.caseTypes
        ?.filter((caseType) => !caseTypeCode || caseType.code === caseTypeCode)
        ?.flatMap((caseType) => [
          // Map case type properties
          ...caseType.properties.map(
            (property) =>
              ({
                propertyCode: mapToPropertyCode(property.code),
                propertyName: property.name,
                dataType: mapDataTypeToAppDataType(property.datatype),
                mandatory: property.mandatory,
                elementCode: null,
                prefix: caseType.name,
                clientType: "case",
              }) satisfies ClientSchemaProperty
          ),
          // Map element properties
          ...caseType.elements.flatMap((element) =>
            element.elementType.properties.map(
              (property) =>
                ({
                  propertyCode: mapToPropertyCode(property.code),
                  propertyName: property.name,
                  dataType: mapDataTypeToAppDataType(property.datatype),
                  mandatory: property.mandatory,
                  elementCode: element.elementType.code,
                  prefix: element.elementType.name,
                  clientType: "case",
                }) satisfies ClientSchemaProperty
            )
          ),
        ]) ?? []),
    ],
    isEmpty: (data) => isEmpty(data?.schema?.caseTypes),
    queryOptions: { onError },
    uiErrors: {
      fetchError: "FETCH-IDENTIFIER-TYPES-ERROR",
      emptyError: "EMPTY-IDENTIFIER-TYPES-ERROR",
    },
  });
};
